import { numberFormat } from "shared/util"
import { Stack, Typography, List, ListItem, ListItemButton, StackProps } from "@mui/material"

import { ClampTypography } from "components/controls/clampTypography"
import { Section } from "components/controls/section"

import { TrialResult } from "interface/trial"
import { useTrialPreview } from "features/trial/preview/container"

type TrialPreviewBodyProps = {
	trial: TrialResult
	container: ReturnType<typeof useTrialPreview>
} & StackProps
export const TrialPreviewBody = ({
	trial,
	container: {
		targetSisetuCode,
		onSisetuOpen,
		targetKojinCode,
		onKojinOpen
	},
	...others
}: TrialPreviewBodyProps) => {
	return (
		<Stack {...others} direction="column">
			{trial.publicTitle && (
				<Section labelWidth="8em" label='平易な研究名称'>
					<Typography variant='body2' component='div'>{trial.publicTitle}</Typography>
				</Section>
			)}
			{trial.sponsor && (
				<Section labelWidth="8em" label='依頼者'>
					<Typography variant='body2' component='div'>{trial.sponsor}</Typography>
				</Section>
			)}
			{trial.countries && (
				<Section labelWidth="8em" label='日本以外の実施国'>
					<Typography variant='body2' component='div'>{trial.countries}</Typography>
				</Section>
			)}
			{trial.sampleSize && (
				<Section labelWidth="8em" label='募集規模'>
					<Typography variant='body2' component='div'>{numberFormat(trial.sampleSize)}人</Typography>
				</Section>
			)}
			{trial.age && (trial.age.lower || trial.age.upper) && (
				<Section labelWidth="8em" label='対象年齢'>
					<Typography variant='body2' component='div'>{trial.age.lower === trial.age.upper ? trial.age.lower : ((trial.age.lower ?? '') + '～' + (trial.age.upper ?? ''))}</Typography>
				</Section>
			)}
			{trial.gender && (
				<Section labelWidth="8em" label='性別'>
					<Typography variant='body2' component='div'>{trial.gender}</Typography>
				</Section>
			)}
			{trial.disease && (
				<Section labelWidth="8em" label='対象疾患'>
					<Typography variant='body2' component='p' whiteSpace='pre-wrap'>{trial.disease}</Typography>
				</Section>
			)}
			{(trial.drugBrandNames || trial.drugGenericNames) && (
				<Section labelWidth="8em" label='医薬品' disablePadding>
					<ClampTypography caption='製品名' text={trial.drugBrandNames}/>
					<ClampTypography caption='一般名' text={trial.drugGenericNames}/>
				</Section>
			)}
			{ trial.includeCriteria && (
				<Section labelWidth="8em" label='選択基準'>
					<ClampTypography text={trial.includeCriteria}/>
				</Section>
			) }
			{ trial.excludeCriteria && (
				<Section labelWidth="8em" label='除外基準'>
					<ClampTypography text={trial.excludeCriteria}/>
				</Section>
			) }
			{ trial.studyPurpose && (
				<Section labelWidth="8em" label='治験の目的'>
					<ClampTypography text={trial.studyPurpose}/>
				</Section>
			) }
			{ trial.interventionDescription && (
				<Section labelWidth="8em" label='介入の内容'>
					<ClampTypography text={trial.interventionDescription}/>
				</Section>
			) }
			{(trial.primaryOutcome || trial.secondaryOutcome) && (
				<Section labelWidth="8em" label='評価項目' disablePadding>
					<ClampTypography caption='主たる評価項目' text={trial.primaryOutcome}/>
					<ClampTypography caption='副次的な評価項目' text={trial.secondaryOutcome}/>
				</Section>
			)}

			{trial.persons && trial.persons.length > 0 && (
				<Section labelWidth="8em" label='医師/担当者' disablePaddingX labelProps={{ py: 1.5 }}>
					<List disablePadding>
						{trial.persons?.map((person, i) =>
							<ListItem key={`person-${i}-${person.kojinCode ?? 'unknown'}`} disablePadding>
								<ListItemButton
									disabled={person.kojinCode ? false : true}
									onClick={() => {
										if (person.kojinCode) onKojinOpen(person.kojinCode)
									}}
									sx={{
										paddingX: 2,
										paddingY: 0.5
									}}
								>
									<Stack direction="column" width='100%'>
										<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
											<Typography
												variant="body2"
											>
												{person.roles.join(', ')}
											</Typography>
											<Typography
												variant="body2"
												fontWeight={person.kojinCode === targetKojinCode ? "bold" : undefined}
											>
												{person.personName}
											</Typography>
											<Typography
												variant="body2"
												fontWeight={person.sisetuCode === targetSisetuCode ? "bold" : undefined}
											>
												{person.facilityName}
											</Typography>
										</Stack>
									</Stack>
								</ListItemButton>
							</ListItem>
						)}
					</List>
				</Section>
			)}

			{trial.sisetu && trial.sisetu.length > 0 && (
				<Section labelWidth="8em" label='実施施設' disablePaddingX labelProps={{ py: 1.5 }}>
					<List disablePadding>
						{trial.sisetu.map((sisetu, i) =>
							<ListItem key={`sisetu-${i}-${sisetu.sisetuCode}`} disablePadding>
								<ListItemButton
									onClick={() => {
										if (sisetu.sisetuCode) onSisetuOpen(sisetu.sisetuCode)
									}}
									sx={{
										paddingX: 2,
										paddingY: 0.5
									}}
								>
									<Stack direction="column" width='100%'>
										<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
											<Typography
												variant="body2"
												fontWeight={sisetu.sisetuCode === targetSisetuCode ? "bold" : undefined}
												color="text.primary"
											>
												{sisetu.sisetuName}
											</Typography>
										</Stack>
									</Stack>
								</ListItemButton>
							</ListItem>
						)}
					</List>
				</Section>
			)}

			<Section labelWidth="8em" flexGrow={1}/>
		</Stack>
	)
}